import { useState, useEffect } from "react";
import { X, Banknote, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { POST_DEBT_PAYMENT_URL } from "../../../features/UrlHelper";
import "./DebtPaymentModal.scss";

function formatAmount(val) {
  return `${Number(val).toFixed(2).replace(".", ",")} €`;
}

function DebtPaymentModal({ customer, onClose, onSuccess }) {
  const currentDebt = Math.abs(parseFloat(customer?.actual_saldo) || 0);
  
  const [amount, setAmount] = useState(() => {
    return currentDebt > 0 ? currentDebt.toFixed(2) : "";
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Resetovať sumu pri zmene dlhu (keď sa otvorí modal pre iného zákazníka)
  useEffect(() => {
    setAmount(currentDebt > 0 ? currentDebt.toFixed(2) : "");
    setError("");
  }, [currentDebt]);

  function validate() {
    const num = parseFloat(String(amount).replace(",", "."));
    if (!amount || isNaN(num) || num <= 0) {
      return "Suma musí byť väčšia ako 0.";
    }
    if (num > currentDebt + 0.001) {
      return `Suma nesmie presiahnuť aktuálny dlh (${formatAmount(currentDebt)}).`;
    }
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await axios.post(
        POST_DEBT_PAYMENT_URL(customer.id),
        { amount: parseFloat(String(amount).replace(",", ".")) }
      );
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Nastala chyba pri zaevidovaní platby.");
    } finally {
      setIsLoading(false);
    }
  }

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="dpayment__overlay" onMouseDown={handleOverlayMouseDown}>
      <div className="dpayment">
        <div className="dpayment__header">
          <h2 className="dpayment__title">Prijať platbu</h2>
          <button className="dpayment__close" onClick={onClose} aria-label="Zavrieť">
            <X size={18} />
          </button>
        </div>

        <div className="dpayment__body">
          <div className="dpayment__info">
            <div className="dpayment__info-row">
              <span className="dpayment__info-label">Zákazník</span>
              <span className="dpayment__info-value">
                {customer?.firstname} {customer?.lastname}
              </span>
            </div>
            <div className="dpayment__info-row">
              <span className="dpayment__info-label">Aktuálny dlh</span>
              <span className="dpayment__info-value dpayment__info-value--debt">
                {formatAmount(currentDebt)}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="dpayment__form">
            <label className="dpayment__label" htmlFor="dpayment-amount">
              Suma na úhradu (€)
            </label>
            <input
              id="dpayment-amount"
              type="number"
              min="0.01"
              step="0.01"
              className={`dpayment__input${error ? " dpayment__input--error" : ""}`}
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(""); }}
              placeholder="0.00"
              autoFocus
            />
            {error && (
              <div className="dpayment__error">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            )}

            <div className="dpayment__actions">
              <button
                type="button"
                className="dpayment__btn dpayment__btn--cancel"
                onClick={onClose}
                disabled={isLoading}
              >
                Zrušiť
              </button>
              <button
                type="submit"
                className="dpayment__btn dpayment__btn--submit"
                disabled={isLoading}
              >
                {isLoading
                  ? <Loader2 size={16} className="dpayment__spinner" />
                  : <Banknote size={16} />}
                <span>{isLoading ? "Zaevidovávam..." : "Zaevidovať platbu"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DebtPaymentModal;
